/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Table,
  Typography,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ManualReviewButtons } from 'components/Transactions/ManualReviewButtons';
import { FileItem, ManualReviewTransactionItem } from '../ManualReview/api';
import { TravelRuleDetailsModal } from './TravelRuleDetailsModal';

interface Props {
  transactions: ManualReviewTransactionItem[];
  refetch: () => void;
}

const headers = [
  { label: 'Transaction ID', cellKey: 'transactionId', sort: false },
  { label: 'User ID', cellKey: 'userId', sort: false },
  { label: 'Type', cellKey: 'type', sort: false },
  { label: 'Currency', cellKey: 'currency', sort: false },
  { label: 'Amount', cellKey: 'amount', sort: false },
  { label: 'Files', cellKey: 'files', sort: false },
  { label: 'Travel Rule Details', cellKey: 'travelRuleDetails', sort: false },
  { label: 'Action', cellKey: 'action', sort: false },
];

export const TransactionsTable = ({ transactions, refetch }: Props) => {
  const styles = useStyles();
  const [selectedTravelRuleData, setSelectedTravelRuleData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [showModal, setShowModal] = useState(false);

  if (transactions.length === 0) {
    return null;
  }

  return (
    <div css={styles.container}>
      {showModal && (
        <TravelRuleDetailsModal
          travelRuleData={selectedTravelRuleData}
          onClose={() => setShowModal(false)}
        />
      )}
      <Typography variant="subtitle">Transactions</Typography>
      <Table
        headers={headers}
        rows={transactions.map((transaction) => ({
          value: {
            transactionId: (
              <div css={styles.idCell}>
                <Link to={`/transactions/${transaction.transactionId}`}>
                  <Typography variant="bodySmallest">
                    {transaction.transactionId}
                  </Typography>
                </Link>
              </div>
            ),
            userId: (
              <div css={styles.idCell}>
                <Typography variant="bodySmallest">
                  {transaction.userId}
                </Typography>
              </div>
            ),
            type: transaction.transactionType,
            currency: transaction.currency,
            amount: Number.parseFloat(transaction.amount).toFixed(5),
            files: <FileList files={transaction.files} />,
            travelRuleDetails: (
              <UtilityButton
                text="Show details"
                variant="secondary"
                onClick={() => {
                  setSelectedTravelRuleData(transaction.travelRuleData);
                  setShowModal(true);
                }}
              />
            ),
            action: (
              <div css={styles.buttonContainer}>
                <ManualReviewButtons
                  transactionId={transaction.transactionId}
                  onApprove={refetch}
                  onReject={refetch}
                />
              </div>
            ),
          },
        }))}
      />
    </div>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  idCell: {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  buttonContainer: {
    '& button': {
      height: '36px',
      padding: `${spacing.xxxs} ${spacing.xxs}`,
      minWidth: '70px',
    },
  },
}));

const FileList = ({ files }: { files: FileItem[] }) => {
  if (files.length === 0) {
    return <Typography variant="bodySmallest">No files</Typography>;
  }
  return (
    <>
      {files.map((file) => (
        <div key={file.name}>
          <a href={file.url} target="_blank" rel="noreferrer">
            {file.name.split('_').slice(1).join('_')}
          </a>
        </div>
      ))}
    </>
  );
};
